'use client';

/**
 * 埋点事件名与《260307 产品需求文档：埋点系统》一致。
 * 属性表在文档嵌入图中未以文本形式提供，此处仅上报明确业务字段。
 */

import posthog from 'posthog-js';

export function canCapture(): boolean {
  return typeof window !== 'undefined' && Boolean(posthog.__loaded);
}

function capture(event: string, properties?: Record<string, unknown>): void {
  // 开发环境关闭埋点上报
  if (!process.env.isProd) return;
  if (!canCapture()) return;
  // 去掉properties中所有为null、undefined、''的字段
  const filteredProperties = Object.fromEntries(
    Object.entries(properties || {}).filter(([_, value]) => value !== null && value !== undefined && value !== '')
  );
  // console.log('capture', event, filteredProperties);
  posthog.capture(event, filteredProperties);
}

const PAGE_VIEW_DEBOUNCE_MS = 200;
let pageViewDebounceTimer: ReturnType<typeof setTimeout> | null = null;
let pageViewPending: { path: string; title?: string; tab?: string } | null = null;

/** 页面浏览（SPA 路由切换），短时连续触发时只上报最后一次 */
export function capturePageView(props: { path: string; title?: string; tab?: string }): void {
  pageViewPending = props;
  if (pageViewDebounceTimer) {
    clearTimeout(pageViewDebounceTimer);
  }
  pageViewDebounceTimer = setTimeout(() => {
    pageViewDebounceTimer = null;
    const p = pageViewPending;
    pageViewPending = null;
    if (!p) return;

    function pathToPageName(path: string): string {
      if (path === '/') return 'home';
      return path.replace(/^\/+/, '').replace(/[/-]/g, '_');
    }

    const pageName = pathToPageName(p.path);

    capture('page_view', {
      ...p,
      page_name: pageName,
    });
  }, PAGE_VIEW_DEBOUNCE_MS);
}

/** 首页发现流 Tab 切换（推荐 / 内容分类） */
export function captureHomeTabClick(props: { category_id: number; tab_name?: string }): void {
  capture('home_tab_click', props);
}

/** 请求结果（业务 `request` 事件） */
export function captureRequest(props: {
  action: string;
  status: 'success' | 'fail';
  code?: number;
  msg?: string;
  type?: string;
  stage?: string;
}): void {
  capture('billion_request', props);
}

/** 登录成功 */
export function captureUserLogin(props: { login_method?: string }): void {
  capture('user_login', props);
}

/** 登录失败 */
export function captureLoginFail(props: { fail_reason?: string; login_method?: string }): void {
  capture('login_fail', props);
}

/** 点击充值（弹层打开时上报，覆盖各入口） */
export function captureDepositClick(props: { type?: 'spot' | 'perps' | 'predict'; source?: string }): void {
  capture('deposit_click', props);
}

/** 点击提现（弹层打开时上报；弹层已打开时切换 Tab 再次上报） */
export function captureWithdrawClick(props: { type?: 'spot' | 'perps' | 'predict'; source?: string }): void {
  capture('withdraw_click', props);
}

/** 支付/充值相关流程结果 */
export function capturePaymentResult(props: {
  status: 'success' | 'fail';
  type: 'spot_to_perps' | 'perps_to_spot' | 'spot_to_spot';
  msg?: string;
}): void {
  capture('payment_result', props);
}

/** 搜索：结果返回后 */
export function captureSearchExecute(props: { search_type: string; query: string }): void {
  capture('search_execute', props);
}

/** 复制邀请链接或邀请码成功 */
export function captureReferralLinkCopy(props: { source: string; type: 'code' | 'link' }): void {
  capture('referral_link_copy', props);
}

/** 点击社交分享（邀请弹窗、邀请页、页脚社媒等） */
export function captureReferralShareClick(props: { source: string; platform: 'x' | 'discord' | 'telegram' | 'line' }): void {
  capture('referral_share_click', props);
}

/** 主播申请表单提交成功（`becomeStreamer` 接口返回成功） */
export function captureStreamerApplyClick(props: { source: string }): void {
  capture('streamer_apply_click', props);
}

/** 创建直播间成功，视为开始直播（含 OBS / WebRTC） */
export function captureStreamStart(props: {
  source: string;
  streaming_mode?: string;
  room_id?: number | string;
  /** 直播标题（对应 roomName） */
  title?: string;
  /** 直播分类（展示名或分类 id 字符串） */
  category?: string;
  /** 当前开播用户 id */
  streamer_id?: number | string;
}): void {
  capture('stream_start', props);
}

/** 主播主动结束直播（stopLiveWithTRTC 成功） */
export function captureStreamEnd(props: { room_id?: number | string }): void {
  capture('stream_end', props);
}

/** 预测市场详情浏览 */
export function capturePredictMarketView(props: { market_id: string }): void {
  capture('predict_market_view', props);
}

/** 预测下注确认成功 */
export function capturePredictBetPlace(props: { market_id?: string; order_id?: string; side?: string; amount?: string }): void {
  capture('predict_bet_place', props);
}

/** ==================== 直播核心 start =================== */
/** 进入直播间 */
export function captureLiveRoomEnter(props: { room_id: string; streamer_id?: string; source?: string }): void {
  props.source = props.source || document.referrer;
  capture('live_room_enter', props);
}

/** 直播间加载耗时 */
export function captureLiveRoomLoadTime(props: { room_id: string; streamer_id?: string; load_duration: number }): void {
  capture('live_stream_load_time', props);
}

/** 离开直播间 */
export function captureLiveRoomExit(props: { room_id: string }): void {
  capture('live_room_exit', props);
}

/** 有效观看 */
export function captureLiveEffectiveWatch(props: { room_id: string; streamer_id?: string; effective_watch: number }): void {
  capture('live_effective_watch', props);
}

/** 关注 */
export function captureFollowClick(props: { target_id: string; action?: string; source?: string }): void {
  capture('follow_click', props);
}

/** 分享 */
export function captureShareClick(props: { content_type: string; content_id?: string; platform?: string }): void {
  capture('share_click', props);
}
/** ==================== 直播核心 end =================== */

/** ==================== 广场 start =================== */
/** 发帖成功 */
// text / image / video / predict / topic,
export function captureSquarePostCreate(props: { content_type: string[] }): void {
  capture('square_post_create', props);
}

/** 帖子互动 */
export function captureSquarePostEngage(props: { post_id: string; action: 'like' | 'comment' | 'share'; author_id: string }): void {
  capture('square_post_engage', props);
}
/** ==================== 广场 end =================== */
